import React, { useCallback, useContext, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import querystring from 'querystring';
import { gql, TSFixMe } from '@yi/core';
import { canViewClasses } from '@yi/core/src/user-access';

import { navigate, SSR } from '~/src/common';
import { useShouldRenderPaywallExperiment } from '~/src/common/experiments';
import { getBrightcoveMediaId } from '~/src/common/media';
import {
  baseArticlesIndexName,
  getAnalyticIndexName,
  getClassStyle,
} from '~/src/common/media-hero';
import { useAccessTier } from '~/src/common/subscription';
import { useConvertedContentFrom } from '~/src/common/sign-up/converted-content-from';
import {
  ClassPaywall,
  IconPlay,
  PlaylistContext,
  useHasMounted,
  useLocale,
} from '~/src/components';
import {
  MediaHeroPlayer,
  MediaHeroPlayerAnalytics,
  MediaHeroPlayerModal,
  MediaHeroPlayerSnackbar,
} from '~/src/components/MediaPlayer';
import type { Content } from '~/src/components/MediaPlayer/media-hero-players/types';

export const IconWrapper = styled.div`
  cursor: pointer;
  text-align: center;
  margin: auto;
  height: 100px;
  z-index: 1;
`;

interface Props {
  content: Content;
}

export const PlayButton: React.FC<Props> = ({ content }) => {
  const { hasMounted } = useHasMounted();

  const [showVideoModal, setShowVideoModal] = useState(false);
  const { shouldRenderPaywall, setShowPaywall } = useShouldRenderPaywallExperiment();

  const { playlistEnabled, playlistId } = useContext(PlaylistContext);

  const { tier } = useAccessTier();
  const hasAccess = canViewClasses(tier);

  const queryParams = !SSR ? querystring.parse(window.location.search.substring(1)) : {};
  const autoplay = queryParams?.autoplay === 'true';

  useEffect(() => {
    if (hasAccess && autoplay) setShowVideoModal(true);
  }, [setShowVideoModal, hasAccess, autoplay]);

  const mediaId = getBrightcoveMediaId(content as TSFixMe);

  const goBack = () =>
    !autoplay
      ? setShowVideoModal(false)
      : playlistEnabled
      ? navigate(`/playlist/?id=${playlistId}`)
      : window.history.back();

  const onPlayerModalClose = () => {
    if (hasAccess) {
      goBack();
    } else {
      setShowVideoModal(false);
      document.body.style.overflow = 'auto';
    }
  };

  const { locale } = useLocale();

  const indexName = getAnalyticIndexName(baseArticlesIndexName, locale);
  const styleName = getClassStyle(content);
  const closePaywallModal = useCallback(() => {
    setShowPaywall(false);
  }, [setShowPaywall]);

  const trackingType = gql.typesClient.LogConsumptionTrackingType.CLASS;

  const { getCurrentPageAsReturnUrl } = useConvertedContentFrom();
  const returnUrl = getCurrentPageAsReturnUrl();

  return (
    <>
      <IconWrapper>
        <IconPlay
          style={{ height: 70, width: 70 }}
          // Prevent tests from interacting with SSR'd element before JS loads
          data-testid={hasMounted ? 'video-play-button' : ''}
          onClick={() => {
            setShowPaywall(true);
            if (!hasAccess) return;
            setShowVideoModal(true);
          }}
        />

        {shouldRenderPaywall && <ClassPaywall onClose={closePaywallModal} returnUrl={returnUrl} />}
      </IconWrapper>

      {mediaId && showVideoModal && (
        <MediaHeroPlayerModal onClose={onPlayerModalClose} open={showVideoModal} content={content}>
          <MediaHeroPlayer content={content}>
            <MediaHeroPlayerAnalytics
              trackingType={gql.typesClient.LogConsumptionTrackingType.CLASS}
              content={content}
              indexName={indexName}
              contentTag={styleName}
            />
            {/*re-mount the component when content has changed*/}
            <MediaHeroPlayerSnackbar key={content.id} trackingType={trackingType} />
          </MediaHeroPlayer>
        </MediaHeroPlayerModal>
      )}
    </>
  );
};
